import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, ScanFace, Swords, Trophy, BookOpen, User, LogOut, Terminal, Sparkles, Crown, MessagesSquare, Flame, FlaskConical } from "lucide-react";
import VipBadge from "@/components/app/VipBadge";

const NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/scan", label: "Scan", icon: ScanFace, testid: "nav-scan" },
  { to: "/events", label: "Mog-Off", icon: Flame, testid: "nav-events" },
  { to: "/chat", label: "Chat", icon: MessagesSquare, testid: "nav-chat" },
  { to: "/coach", label: "Coach", icon: Sparkles, testid: "nav-coach" },
  { to: "/peptides", label: "Peptides", icon: FlaskConical, testid: "nav-peptides" },
  { to: "/battle", label: "Battle", icon: Swords, testid: "nav-battle" },
  { to: "/leaderboard", label: "Leaders", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/guides", label: "Guides", icon: BookOpen, testid: "nav-guides" },
  { to: "/vip", label: "VIP", icon: Crown, testid: "nav-vip", accent: true },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)] text-[var(--text)] relative z-10">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-[var(--border)] bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
              <Terminal className="w-5 h-5 text-[var(--primary)] glow" strokeWidth={2.5} />
              <span className="font-display text-2xl tracking-wider glow">ASCEND</span>
            </NavLink>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map(({ to, label, icon: Icon, testid, accent }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors flex items-center gap-2 border-b-2 ${
                      isActive
                        ? "border-[var(--primary)] text-white"
                        : accent
                        ? "border-transparent text-[var(--primary)] glow hover:text-white"
                        : "border-transparent text-[var(--text-muted)] hover:text-white"
                    }`
                  }
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono">
              <span className="text-[var(--text-muted)]">ELO</span>
              <span className="text-white">{user?.elo ?? 1000}</span>
            </div>
            <div className="hidden sm:block text-xs font-mono text-[var(--text-muted)]">
              @{user?.username}
            </div>
            <VipBadge user={user} size="sm" />
            {user?.avatar_base64 ? (
              <div className="w-7 h-7 border border-[var(--border-bright)] overflow-hidden">
                <img src={`data:image/jpeg;base64,${user.avatar_base64}`} alt="avatar" className="w-full h-full object-cover" data-testid="nav-avatar" />
              </div>
            ) : null}
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Mobile nav strip */}
        <nav className="md:hidden flex items-center gap-1 px-3 py-2 border-t border-[var(--border)] overflow-x-auto scroll-x">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 whitespace-nowrap ${
                  isActive ? "text-[var(--primary)]" : "text-[var(--text-muted)]"
                }`
              }
            >
              <Icon className="w-3 h-3" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--border)] mt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-6 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex justify-between">
          <span>ASCEND // LOOKSMAXX PROTOCOL v1.0</span>
          <span>STAY ASCENDING</span>
        </div>
      </footer>
    </div>
  );
}
